import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Target, ChartBar, Lightning, CaretDown, Faders, Play } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const tierColors = {
  H: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', badge: 'bg-green-100 text-green-800', fill: 'bg-green-600' },
  M: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-800', fill: 'bg-amber-500' },
  L: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', badge: 'bg-red-100 text-red-800', fill: 'bg-red-500' },
};

const signalColors = {
  ACTIVE: 'bg-green-100 text-green-800',
  DUE: 'bg-red-100 text-red-800',
  MIXED: 'bg-gray-100 text-gray-600',
  NEUTRAL: 'bg-gray-100 text-gray-400',
};

const PatternBadge = ({ pattern }) => (
  <div className="flex gap-0.5" data-testid={`pattern-${pattern}`}>
    {(pattern || '').split('').map((c, i) => (
      <span key={i} className={`inline-flex items-center justify-center w-7 h-7 rounded text-xs font-bold ${tierColors[c]?.badge || 'bg-gray-100'}`}>{c}</span>
    ))}
  </div>
);

const tagColors = { 'H2': 'text-amber-600', 'H3': 'text-orange-600', 'H4': 'text-red-600', 'H1': 'text-green-700', 'M-only': 'text-blue-600', 'L-only': 'text-gray-400' };

const EMPTY_SET = {
  hml_pattern: '',
  sign_pattern: '',
  combo_pattern: '',
  sum_range_min: '',
  sum_range_max: '',
  range_pattern: '',
  shared_prediction: '',
  remarks_criteria: '',
  remarks_based_on: '',
};

/* ─── Dependency helpers ─── */
const SIGN_TO_DIR = { '+': 'UP', '-': 'DOWN', '0': 'SAME' };
const DIR_TO_SIGN = { 'UP': '+', 'DOWN': '-', 'SAME': '0' };

function signToCombo(signPattern, existingCombo) {
  // Convert W3 sign "++-++" to W5 combo "UP,UP,DOWN,UP,UP" for B1-B5, preserving LS1/LS2
  const signs = signPattern.replace(/,/g, '');
  const dirs = signs.split('').map(s => SIGN_TO_DIR[s] || 'UP');
  // Preserve existing LS1/LS2 positions if combo already has them
  const existing = (existingCombo || '').split(',').map(s => s.trim());
  const ls1 = existing[5] || '';
  const ls2 = existing[6] || '';
  const parts = [...dirs];
  if (ls1) parts.push(ls1);
  if (ls2) parts.push(ls2);
  return parts.join(',');
}

function comboToSign(comboPattern) {
  // Convert W5 combo "UP,UP,DOWN,UP,UP,..." to W3 sign "++-++" (B1-B5 only)
  const parts = comboPattern.split(',').map(s => s.trim().toUpperCase());
  return parts.slice(0, 5).map(d => DIR_TO_SIGN[d] || '+').join('');
}

function rangePatternToSumRange(rangePattern) {
  // Convert W24 range pattern(s) to combined [minSum, maxSum]
  // Supports multiple patterns separated by '|' — takes union (min of mins, max of maxes)
  const ranges = [[1, 10], [11, 20], [21, 30], [31, 40], [41, 50]];
  const patterns = rangePattern.split('|').map(s => s.trim()).filter(Boolean);
  if (patterns.length === 0) return null;

  let globalMin = Infinity, globalMax = -Infinity;
  let validCount = 0;
  for (const pat of patterns) {
    const parts = pat.split('-').map(Number);
    if (parts.length !== 5 || parts.some(isNaN)) continue;
    if (parts.reduce((a, b) => a + b, 0) !== 5) continue;
    let minSum = 0, maxSum = 0;
    for (let i = 0; i < 5; i++) {
      const count = parts[i];
      const [lo, hi] = ranges[i];
      for (let j = 0; j < count; j++) { minSum += lo + j; }
      for (let j = 0; j < count; j++) { maxSum += hi - j; }
    }
    globalMin = Math.min(globalMin, minSum);
    globalMax = Math.max(globalMax, maxSum);
    validCount++;
  }
  if (validCount === 0) return null;
  return { min: globalMin, max: globalMax, count: validCount };
}

const DepBadge = ({ type, synced }) => {
  if (type === 'dependent') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-200" data-testid="dep-badge">
        DEPENDENT {synced && <span className="text-orange-500">— synced with {synced}</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-50 text-sky-600 border border-sky-200" data-testid="indep-badge">
      INDEPENDENT
    </span>
  );
};

/* ─── Constraint Input Form for a single C set ─── */
function ConstraintForm({ setKey, data, onChange }) {
  const update = (field, value) => {
    let newData = { ...data, [field]: value };

    // ═══ AUTO-SYNC: W3 → W5 ═══
    if (field === 'sign_pattern' && value) {
      const clean = value.replace(/,/g, '');
      if (clean.length >= 5 && /^[+\-0]{5}$/.test(clean)) {
        newData.combo_pattern = signToCombo(value, data.combo_pattern);
      }
    }
    // ═══ AUTO-SYNC: W5 → W3 ═══
    if (field === 'combo_pattern' && value) {
      const parts = value.split(',').map(s => s.trim().toUpperCase());
      if (parts.length >= 5 && parts.slice(0, 5).every(d => ['UP', 'DOWN', 'SAME'].includes(d))) {
        newData.sign_pattern = comboToSign(value);
      }
    }
    // ═══ AUTO-SYNC: W24 → W14 ═══
    if (field === 'range_pattern' && value) {
      const sumRange = rangePatternToSumRange(value);
      if (sumRange) {
        newData.sum_range_min = String(sumRange.min);
        newData.sum_range_max = String(sumRange.max);
      }
    }

    onChange(setKey, newData);
  };

  // Compute implied sum range for display
  const impliedSum = data.range_pattern ? rangePatternToSumRange(data.range_pattern) : null;
  const sumOverridden = impliedSum && (
    (data.sum_range_min && String(data.sum_range_min) !== String(impliedSum.min)) ||
    (data.sum_range_max && String(data.sum_range_max) !== String(impliedSum.max))
  );

  return (
    <div className="space-y-3" data-testid={`${setKey}-form`}>
      {/* W7 HML Pattern */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase">W7 — HML Pattern</span>
            <DepBadge type="independent" />
          </div>
          {data.hml_pattern && <PatternBadge pattern={data.hml_pattern} />}
        </div>
        <div className="grid grid-cols-5 gap-2">
          {['Ball 1', 'Ball 2', 'Ball 3', 'Ball 4', 'Ball 5'].map((label, i) => {
            const current = (data.hml_pattern || 'HHHHH')[i] || 'H';
            return (
              <div key={i} className="text-center">
                <p className="text-[9px] text-gray-400 mb-1">{label}</p>
                <div className="flex gap-0.5 justify-center">
                  {['H', 'M', 'L'].map(tier => {
                    const isActive = current === tier;
                    const color = tier === 'H' ? (isActive ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 border border-green-200')
                      : tier === 'M' ? (isActive ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 border border-amber-200')
                      : (isActive ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 border border-red-200');
                    return (
                      <button key={tier} onClick={() => {
                        const arr = (data.hml_pattern || 'HHHHH').split('');
                        arr[i] = tier;
                        update('hml_pattern', arr.join(''));
                      }}
                        className={`w-8 h-8 rounded font-bold text-xs transition ${color}`}
                        data-testid={`${setKey}-hml-pos${i}-${tier}`}>{tier}</button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Remarks */}
      {(data.remarks_criteria || data.remarks_based_on) && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[10px] text-blue-800">
          {data.remarks_criteria && <span className="font-bold">Criteria:</span>} {data.remarks_criteria}
          {data.remarks_based_on && <span className="ml-2 font-bold">Based on:</span>} {data.remarks_based_on}
        </div>
      )}

      {/* W3 & W5 — DEPENDENT pair */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <label className="text-[10px] font-bold text-gray-500">W3 — Sign Pattern</label>
            <DepBadge type="dependent" synced="W5" />
          </div>
          <input type="text" value={data.sign_pattern || ''} onChange={e => update('sign_pattern', e.target.value)}
            placeholder="e.g. ++-++" className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-orange-400"
            data-testid={`${setKey}-sign-pattern`} />
          <p className="text-[9px] text-orange-500 mt-0.5">+ = UP, - = DOWN, 0 = SAME — auto-syncs W5 B1-B5</p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <label className="text-[10px] font-bold text-gray-500">W5 — Combo Pattern</label>
            <DepBadge type="dependent" synced="W3" />
          </div>
          <input type="text" value={data.combo_pattern || ''} onChange={e => update('combo_pattern', e.target.value)}
            placeholder="e.g. UP,UP,DOWN,UP,UP,DOWN,UP" className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-orange-400"
            data-testid={`${setKey}-combo-pattern`} />
          <p className="text-[9px] text-orange-500 mt-0.5">B1-B5 synced from W3 — LS1,LS2 set independently</p>
        </div>
      </div>

      {/* W24 & W14 — DERIVED pair */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <label className="text-[10px] font-bold text-gray-500">W24 — Range Pattern(s)</label>
            <DepBadge type="dependent" synced="W14" />
          </div>
          {/* Show existing patterns as chips */}
          {(() => {
            const patterns = (data.range_pattern || '').split('|').map(s => s.trim()).filter(Boolean);
            const addPatternPP = (val) => {
              if (!val) return;
              const trimmed = val.trim();
              const parts = trimmed.split('-');
              if (parts.length !== 5) { toast.error(`Pattern must have 5 numbers (L-ML-M-MH-H). Got ${parts.length}.`); return; }
              const nums = parts.map(Number);
              if (nums.some(isNaN)) { toast.error('Pattern must contain only numbers separated by dashes'); return; }
              if (nums.reduce((a, b) => a + b, 0) !== 5) { toast.error(`Numbers must sum to 5 (got ${nums.reduce((a, b) => a + b, 0)})`); return; }
              if (patterns.includes(trimmed)) { toast.error('Pattern already added'); return; }
              const newPats = [...patterns, trimmed];
              update('range_pattern', newPats.join('|'));
            };
            return (
              <>
                {patterns.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    {patterns.map((pat, idx) => {
                      const sr = rangePatternToSumRange(pat);
                      return (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 border border-orange-200 rounded text-[10px] font-mono font-bold text-orange-800">
                          {pat}
                          {sr && <span className="text-[8px] text-orange-500">({sr.min}–{sr.max})</span>}
                          <button onClick={() => {
                            const newPats = patterns.filter((_, i) => i !== idx);
                            update('range_pattern', newPats.join('|'));
                          }} className="ml-0.5 text-red-400 hover:text-red-600 font-bold" data-testid={`${setKey}-remove-range-${idx}`}>&times;</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-1">
                  <input type="text" defaultValue=""
                    placeholder={patterns.length > 0 ? 'Add e.g. 0-2-1-1-1' : 'e.g. 1-1-2-1-0'}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-orange-400"
                    data-testid={`${setKey}-range-pattern`}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        addPatternPP(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button onClick={(e) => {
                    const inp = e.target.closest('.flex')?.querySelector('input');
                    if (inp) { addPatternPP(inp.value); inp.value = ''; }
                  }} className="px-2 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs font-bold transition"
                    data-testid={`${setKey}-add-range`}>+</button>
                </div>
                <p className="text-[9px] text-orange-500 mt-0.5">
                  L(1-10)-ML(11-20)-M(21-30)-MH(31-40)-H(41-50) — must total 5. Press Enter or click +.
                  {patterns.length > 1 && <span className="font-bold ml-1">OR logic: matches any pattern.</span>}
                </p>
              </>
            );
          })()}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <label className="text-[10px] font-bold text-gray-500">W14 — Sum Range</label>
            <DepBadge type="dependent" synced="W24" />
          </div>
          <div className="flex gap-1">
            <input type="number" value={data.sum_range_min || ''} onChange={e => update('sum_range_min', e.target.value)}
              placeholder="Min" className={`w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:border-orange-400 ${impliedSum && !sumOverridden ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
              data-testid={`${setKey}-sum-min`} />
            <input type="number" value={data.sum_range_max || ''} onChange={e => update('sum_range_max', e.target.value)}
              placeholder="Max" className={`w-full px-2 py-1.5 border rounded text-xs focus:outline-none focus:border-orange-400 ${impliedSum && !sumOverridden ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
              data-testid={`${setKey}-sum-max`} />
          </div>
          {impliedSum && (
            <p className="text-[9px] mt-0.5">
              <span className="text-orange-600 font-bold">Auto from W24{impliedSum.count > 1 ? ` (${impliedSum.count} patterns)` : ''}: {impliedSum.min}–{impliedSum.max}</span>
              {sumOverridden && <span className="text-red-500 ml-1">(overridden)</span>}
            </p>
          )}
        </div>
      </div>

      {/* Independent constraints */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <label className="text-[10px] font-bold text-gray-500">W17 — Shared Numbers</label>
            <DepBadge type="independent" />
          </div>
          <input type="text" value={data.shared_prediction || ''} onChange={e => update('shared_prediction', e.target.value)}
            placeholder="e.g. 0,1,2" className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
            data-testid={`${setKey}-shared`} />
        </div>
      </div>
    </div>
  );
}

/* ─── Ticket Results Table ─── */
function TicketTable({ tickets, setKey, rangePatterns }) {
  if (!tickets || tickets.length === 0) return null;
  const hasGroups = rangePatterns && rangePatterns.length > 1;

  // Group tickets by range_group
  const groups = hasGroups ? rangePatterns.map(rp => ({
    pattern: rp,
    tickets: tickets.filter(t => t.range_group === rp),
  })) : [{ pattern: null, tickets }];

  return (
    <div className="space-y-4" data-testid={`${setKey}-results`}>
      {groups.map((group, gIdx) => (
        <div key={gIdx}>
          {hasGroups && (
            <div className="flex items-center gap-2 mb-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2" data-testid={`${setKey}-group-${gIdx}`}>
              <span className="text-[10px] font-bold text-indigo-800 uppercase">Range Pattern {gIdx + 1}:</span>
              <span className="font-mono font-bold text-sm text-indigo-700">{group.pattern}</span>
              <span className="text-[10px] text-indigo-500">{group.tickets.length} tickets</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-2 py-2 text-left font-bold text-gray-500">#</th>
                  {['B1', 'B2', 'B3', 'B4', 'B5'].map(h => <th key={h} className="px-2 py-2 text-center font-bold text-gray-500">{h}</th>)}
                  <th className="px-2 py-2 text-center font-bold text-gray-500">LS1</th>
                  <th className="px-2 py-2 text-center font-bold text-gray-500">LS2</th>
                  <th className="px-2 py-2 text-center font-bold text-gray-500">HML</th>
                  <th className="px-2 py-2 text-center font-bold text-gray-500">D/P</th>
                  <th className="px-2 py-2 text-right font-bold text-gray-500">Score</th>
                </tr>
              </thead>
              <tbody>
                {group.tickets.map((t, idx) => (
                  <tr key={idx} className={idx === 0 ? 'bg-emerald-50' : idx % 2 === 0 ? 'bg-gray-50/50' : ''} data-testid={`${setKey}-ticket-${gIdx * 10 + idx}`}>
                    <td className="px-2 py-2 font-bold text-gray-400">{idx + 1}</td>
                    {(t.ball_profiles || []).map((bp, j) => (
                      <td key={j} className="px-2 py-2 text-center">
                        <span className="font-mono font-bold text-gray-900">{bp.number}</span>
                        <span className={`text-[8px] ml-0.5 font-bold ${tagColors[bp.tag] || 'text-gray-400'}`}>{bp.tag}</span>
                      </td>
                    ))}
                    {!t.ball_profiles && (t.balls || []).map((b, j) => (
                      <td key={j} className="px-2 py-2 text-center font-mono font-bold text-gray-900">{b}</td>
                    ))}
                    {(t.lucky_stars || []).map((ls, j) => <td key={j} className="px-2 py-2 text-center font-mono font-bold text-amber-600">{ls}</td>)}
                    <td className="px-2 py-2 text-center text-[10px] font-bold">{t.hml_pattern || '-'}</td>
                    <td className="px-2 py-2 text-center text-[10px] font-bold text-indigo-700">{t.composition || '-'}</td>
                    <td className="px-2 py-2 text-right font-mono font-bold text-indigo-700">{t.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Main Pattern Predictor Page ─── */
export default function PatternPredictor() {
  const navigate = useNavigate();
  const [indicatorData, setIndicatorData] = useState(null);
  const [indicatorLoading, setIndicatorLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSet, setActiveSet] = useState('C1');

  // Constraint sets state
  const [constraintSets, setConstraintSets] = useState({
    C1: { ...EMPTY_SET },
    C2: { ...EMPTY_SET },
    C3: { ...EMPTY_SET },
    C4: { ...EMPTY_SET },
    C5: { ...EMPTY_SET },
  });
  const [results, setResults] = useState({ C1: null, C2: null, C3: null, C4: null, C5: null });
  const [generating, setGenerating] = useState({ C1: false, C2: false, C3: false, C4: false, C5: false });
  const [drawRef, setDrawRef] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [constraintsLoaded, setConstraintsLoaded] = useState(false);

  // Load indicator data + persisted constraints on mount
  useEffect(() => {
    axios.get(`${API}/analysis/pattern-predictor`)
      .then(res => { setIndicatorData(res.data); setIndicatorLoading(false); })
      .catch(() => { toast.error('Failed to load indicators'); setIndicatorLoading(false); });

    axios.get(`${API}/analysis/constraint-sets`)
      .then(res => {
        const d = res.data;
        setDrawRef(d.draw_reference);
        setIsStale(d.is_stale);
        const newSets = { ...constraintSets };
        ['C1', 'C2', 'C3', 'C4', 'C5'].forEach((key, i) => {
          if (d[key]) {
            newSets[key] = {
              hml_pattern: d[key].hml_pattern || '',
              sign_pattern: d[key].sign_pattern || '',
              combo_pattern: d[key].combo_pattern || '',
              sum_range_min: d[key].sum_range_min != null ? String(d[key].sum_range_min) : '',
              sum_range_max: d[key].sum_range_max != null ? String(d[key].sum_range_max) : '',
              range_pattern: d[key].range_pattern || '',
              shared_prediction: d[key].shared_prediction || '',
              remarks_criteria: d[key].remarks_criteria || '',
              remarks_based_on: d[key].remarks_based_on || '',
            };
          }
        });
        setConstraintSets(newSets);
        setConstraintsLoaded(true);
      })
      .catch(() => { setConstraintsLoaded(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSet = useCallback((setKey, newData) => {
    setConstraintSets(prev => ({ ...prev, [setKey]: newData }));
  }, []);

  const saveAndGenerate = async (setKey) => {
    setGenerating(prev => ({ ...prev, [setKey]: true }));
    try {
      // Save constraint sets using PATCH to preserve fields set by other windows (temp_prefs, number_picks)
      for (const k of ['C1', 'C2', 'C3', 'C4', 'C5']) {
        const s = constraintSets[k];
        await axios.patch(`${API}/analysis/constraint-sets`, {
          set_key: k,
          fields: {
            hml_pattern: s.hml_pattern || null,
            sign_pattern: s.sign_pattern || null,
            combo_pattern: s.combo_pattern || null,
            sum_range_min: s.sum_range_min ? parseInt(s.sum_range_min) : null,
            sum_range_max: s.sum_range_max ? parseInt(s.sum_range_max) : null,
            range_pattern: s.range_pattern || null,
            shared_prediction: s.shared_prediction || null,
            remarks_criteria: s.remarks_criteria || '',
            remarks_based_on: s.remarks_based_on || '',
          },
        });
      }

      // Generate tickets for this set
      const s = constraintSets[setKey];
      const genPayload = {
        hml_pattern: s.hml_pattern || null,
        sign_pattern: s.sign_pattern || null,
        combo_pattern: s.combo_pattern || null,
        sum_range_min: s.sum_range_min ? parseInt(s.sum_range_min) : null,
        sum_range_max: s.sum_range_max ? parseInt(s.sum_range_max) : null,
        range_pattern: s.range_pattern || null,
        shared_prediction: s.shared_prediction || null,
      };
      const res = await axios.post(`${API}/analysis/pattern-predictor/generate`, genPayload);
      setResults(prev => ({ ...prev, [setKey]: res.data }));
      const count = res.data.combinations?.length || 0;
      toast.success(`${setKey}: Generated ${count} tickets`);
    } catch (err) {
      toast.error(`Failed to generate ${setKey} tickets`);
    }
    setGenerating(prev => ({ ...prev, [setKey]: false }));
  };

  const generateAll = async () => {
    for (const key of ['C1', 'C2', 'C3', 'C4', 'C5']) {
      await saveAndGenerate(key);
    }
  };

  const ballLabels = ['B1', 'B2', 'B3', 'B4', 'B5'];
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  const countActiveConstraints = (s) => {
    let count = 0;
    if (s.hml_pattern) count++;
    if (s.sign_pattern) count++;
    if (s.combo_pattern) count++;
    if (s.sum_range_min || s.sum_range_max) count++;
    if (s.range_pattern) count++;
    if (s.shared_prediction) count++;
    return count;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-gray-100" data-testid="back-btn"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">W54: Pattern Predictor</h1>
              <p className="text-xs text-gray-500">3 indicators + C1/C2/C3 constraint-driven ticket generation</p>
            </div>
          </div>
          {isStale && <span className="text-xs font-bold text-amber-600 bg-amber-100 px-3 py-1 rounded-lg">New draw since last save — constraints may be stale</span>}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <WindowNarrative windowId="W54" {...NARRATIVES.W54} className="mb-5" />
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 border-b border-gray-200 pb-0" data-testid="pp-tabs">
          <button onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${activeTab === 'overview' ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            data-testid="tab-overview">
            <ChartBar size={16} /> Indicators
          </button>
          {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => {
            const n = countActiveConstraints(constraintSets[key]);
            const hasResults = results[key]?.combinations?.length > 0;
            return (
              <button key={key} onClick={() => { setActiveTab('constraints'); setActiveSet(key); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${activeTab === 'constraints' && activeSet === key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                data-testid={`tab-${key}`}>
                <Faders size={16} /> {key}
                {n > 0 && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{n}</span>}
                {hasResults && <span className="w-2 h-2 rounded-full bg-green-500" />}
              </button>
            );
          })}
        </div>

        {/* ─── Overview Tab (3 Indicators) ─── */}
        {activeTab === 'overview' && (
          indicatorLoading ? (
            <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
          ) : !indicatorData ? (
            <div className="text-center py-10 text-gray-500">Failed to load</div>
          ) : (
            <div className="space-y-6">
              {/* Recommended Pattern Banner */}
              <div className="bg-gradient-to-r from-[#002FA7] to-[#001d6a] rounded-xl p-6 text-white" data-testid="recommended-banner">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-xs text-white/60 uppercase font-bold tracking-wider">Combined Recommendation</p>
                    <p className="text-[10px] text-white/40 mt-1">Based on all 3 indicators weighted: Tier Deficit 40%, Pattern Deficit 30%, Recency Signal 30%</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <PatternBadge pattern={indicatorData.recommended_pattern} />
                    <div className="text-right">
                      <p className="text-2xl font-black">{indicatorData.recommended_pattern}</p>
                      <p className="text-[10px] text-white/50">Draw #{indicatorData.last_draw?.draw_number}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-3 mt-5">
                  {ballKeys.map((bk, i) => {
                    const rec = indicatorData.recommendation?.[bk] || {};
                    const scores = rec.scores || {};
                    return (
                      <div key={bk} className="bg-white/10 rounded-lg p-3" data-testid={`rec-${bk}`}>
                        <p className="text-[10px] text-white/50 font-bold">{ballLabels[i]}</p>
                        <p className={`text-xl font-black mt-1 ${tierColors[rec.recommended_tier]?.text || ''}`}>
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${tierColors[rec.recommended_tier]?.badge || 'bg-white/20'}`}>
                            {rec.recommended_tier}
                          </span>
                        </p>
                        <div className="mt-2 space-y-0.5">
                          {['H', 'M', 'L'].map(t => (
                            <div key={t} className="flex justify-between text-[10px]">
                              <span className="text-white/40">{t}</span>
                              <span className={t === rec.recommended_tier ? 'text-white font-bold' : 'text-white/30'}>{scores[t]?.combined || 0}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Indicator 1: Tier Deficit */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="indicator-1">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">1</span>
                  <div>
                    <h3 className="font-bold text-gray-900">Indicator 1: Tier Deficit</h3>
                    <p className="text-[10px] text-gray-400">Which H/M/L tier is most lacking in {indicatorData.current_year} vs historical per position</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs text-gray-500">Suggested pattern:</span>
                    <PatternBadge pattern={indicatorData.suggested_pattern} />
                  </div>
                  <div className="grid grid-cols-5 gap-4">
                    {ballKeys.map((bk, i) => {
                      const i1 = indicatorData.indicator_1?.[bk] || {};
                      return (
                        <div key={bk} className={`rounded-lg p-3 border ${tierColors[i1.most_lacking]?.border} ${tierColors[i1.most_lacking]?.bg}`} data-testid={`i1-${bk}`}>
                          <p className="text-[10px] text-gray-500 font-bold uppercase">{ballLabels[i]}</p>
                          <p className={`text-2xl font-black mt-1 ${tierColors[i1.most_lacking]?.text}`}>{i1.most_lacking}</p>
                          <p className="text-xs text-gray-500 mt-1">{i1.deficit}%</p>
                          <div className="mt-2 space-y-1">
                            {(i1.all_tiers || []).map(t => (
                              <div key={t.tier} className="flex justify-between text-[10px]">
                                <span className={tierColors[t.tier]?.text}>{t.tier}</span>
                                <span className={t.diff < -2 ? 'text-red-600 font-bold' : t.diff > 2 ? 'text-blue-600' : 'text-gray-400'}>
                                  {t.diff > 0 ? '+' : ''}{t.diff}%
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Indicator 2: Lacking Patterns */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="indicator-2">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center text-red-700 font-bold text-xs">2</span>
                  <div>
                    <h3 className="font-bold text-gray-900">Indicator 2: Lacking Patterns</h3>
                    <p className="text-[10px] text-gray-400">HML patterns under-represented in {indicatorData.current_year} vs historical</p>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-bold text-red-800 mb-2 flex items-center gap-1"><CaretDown size={12} weight="bold" /> Lacking in {indicatorData.current_year}</p>
                    <div className="space-y-1.5">
                      {(indicatorData.indicator_2?.lacking || []).map((p, idx) => (
                        <div key={p.pattern} className="flex items-center justify-between bg-red-50 rounded px-3 py-1.5 border border-red-100" data-testid={`lacking-${idx}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-gray-400 w-4">{idx + 1}</span>
                            <PatternBadge pattern={p.pattern} />
                          </div>
                          <div className="text-right text-[10px]">
                            <span className="text-gray-500">Hist: <strong>{p.hist_pct}%</strong></span>
                            <span className="mx-1 text-gray-300">|</span>
                            <span className="text-red-600 font-bold">{p.diff}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-purple-800 mb-2 flex items-center gap-1"><ChartBar size={12} weight="bold" /> Missing in {indicatorData.current_year} (0 appearances)</p>
                    <div className="space-y-1.5">
                      {(indicatorData.indicator_2?.zero_in_cy || []).map((p, idx) => (
                        <div key={p.pattern} className="flex items-center justify-between bg-purple-50 rounded px-3 py-1.5 border border-purple-100" data-testid={`zero-${idx}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-gray-400 w-4">{idx + 1}</span>
                            <PatternBadge pattern={p.pattern} />
                          </div>
                          <span className="text-[10px] text-gray-500">Hist: <strong>{p.hist_pct}%</strong> ({p.hist_count}x)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Indicator 3: Recency-Weighted Tier Signal */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="indicator-3">
                <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-xs">3</span>
                  <div>
                    <h3 className="font-bold text-gray-900">Indicator 3: Recency-Weighted Tier Signal</h3>
                    <p className="text-[10px] text-gray-400">Within each H/M/L tier, how many numbers are Hot (last 10), Warm (last 20), or Cold. Signals: ACTIVE = many hot, DUE = mostly cold, MIXED = balanced</p>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {ballKeys.map((bk, bIdx) => {
                    const i3 = indicatorData.indicator_3?.[bk] || {};
                    return (
                      <div key={bk} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`i3-${bk}`}>
                        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-[#002FA7] flex items-center justify-center text-white font-bold text-xs">{ballLabels[bIdx]}</div>
                          <span className="text-xs font-bold text-gray-700">Ball {bIdx + 1}</span>
                        </div>
                        <div className="grid grid-cols-3 divide-x divide-gray-200">
                          {['H', 'M', 'L'].map(tier => {
                            const tr = i3[tier] || {};
                            const tc = tierColors[tier];
                            return (
                              <div key={tier} className="p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`px-2 py-0.5 rounded font-bold text-xs ${tc.badge}`}>{tier === 'H' ? 'High' : tier === 'M' ? 'Medium' : 'Low'}</span>
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${signalColors[tr.signal] || 'bg-gray-100'}`}>{tr.signal}</span>
                                </div>
                                <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-2">
                                  {tr.total > 0 && (
                                    <>
                                      {tr.hot_count > 0 && <div className="bg-red-500" style={{ width: `${tr.hot_count / tr.total * 100}%` }} />}
                                      {tr.warm_count > 0 && <div className="bg-amber-400" style={{ width: `${tr.warm_count / tr.total * 100}%` }} />}
                                      {tr.cold_count > 0 && <div className="bg-blue-400" style={{ width: `${tr.cold_count / tr.total * 100}%` }} />}
                                    </>
                                  )}
                                </div>
                                <div className="space-y-1 text-[10px]">
                                  <div className="flex justify-between"><span className="text-red-600">Hot</span><span className="font-bold">{tr.hot_count}/{tr.total} ({tr.hot_pct || 0}%)</span></div>
                                  <div className="flex justify-between"><span className="text-amber-600">Warm</span><span className="font-bold">{tr.warm_count}/{tr.total}</span></div>
                                  <div className="flex justify-between"><span className="text-blue-600">Cold</span><span className="font-bold">{tr.cold_count}/{tr.total} ({tr.cold_pct || 0}%)</span></div>
                                  <div className="flex justify-between"><span className="text-gray-500">Avg Gap</span><span className="font-mono">{tr.avg_gap}</span></div>
                                  <div className="flex justify-between"><span className="text-gray-500">Score</span><span className="font-mono font-bold">{tr.recency_score}</span></div>
                                </div>
                                {tr.cold_nums && tr.cold_nums.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-[9px] text-gray-400 mb-1">Top cold (overdue):</p>
                                    <div className="flex flex-wrap gap-1">
                                      {tr.cold_nums.slice(0, 5).map(n => (
                                        <span key={n.number} className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[9px] font-mono font-bold text-blue-800">
                                          {n.number}<span className="text-blue-400 ml-0.5">g{n.gap}</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )
        )}

        {/* ─── Constraint Set Tabs (C1/C2/C3) ─── */}
        {activeTab === 'constraints' && (
          <div className="space-y-4">
            {/* Set Switcher */}
            <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-3 flex-wrap">
              {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => {
                const n = countActiveConstraints(constraintSets[key]);
                const hasResults = results[key]?.combinations?.length > 0;
                return (
                  <button key={key} onClick={() => setActiveSet(key)}
                    className={`px-5 py-2.5 rounded font-bold text-sm transition flex items-center gap-2 ${activeSet === key ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'}`}
                    data-testid={`set-${key}`}>
                    {key}
                    {n > 0 && <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">{n} constraints</span>}
                    {hasResults && <span className="w-2 h-2 rounded-full bg-green-400" />}
                  </button>
                );
              })}
              <button onClick={generateAll} disabled={generating.C1 || generating.C2 || generating.C3}
                className="ml-auto px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold text-sm transition disabled:opacity-50 flex items-center gap-2"
                data-testid="generate-all-btn">
                {(generating.C1 || generating.C2 || generating.C3) ? <CircleNotch size={16} className="animate-spin" /> : <Play size={16} weight="fill" />}
                Generate All (C1+C2+C3)
              </button>
            </div>

            {/* Active Constraint Form */}
            {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => {
              if (key !== activeSet) return null;
              return (
                <div key={key} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900">{key} — Constraint Configuration</h3>
                      <p className="text-[10px] text-gray-400">Select constraints from different windows. All combined produce 10 tickets.</p>
                    </div>
                    <button onClick={() => saveAndGenerate(key)} disabled={generating[key]}
                      className="px-5 py-2.5 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-lg font-bold text-sm transition disabled:opacity-50 flex items-center gap-2"
                      data-testid={`generate-${key}-btn`}>
                      {generating[key] ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
                      Save & Generate {key}
                    </button>
                  </div>
                  <div className="p-4">
                    <ConstraintForm setKey={key} data={constraintSets[key]} onChange={updateSet} />
                  </div>
                </div>
              );
            })}

            {/* Results for active set */}
            {results[activeSet] && results[activeSet].combinations?.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid={`${activeSet}-no-results`}>
                <p className="text-sm font-bold text-amber-800">No tickets generated</p>
                <p className="text-xs text-amber-600 mt-1">Constraints too restrictive — try relaxing some (remove sign pattern, reduce range patterns, etc.)</p>
              </div>
            )}
            {results[activeSet]?.combinations?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`${activeSet}-results-section`}>
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900">{activeSet} Results — {results[activeSet].combinations.length} Tickets{results[activeSet].range_pattern_count > 1 ? ` (${results[activeSet].range_pattern_count} range patterns × 10)` : ''}</h3>
                    <p className="text-[10px] text-gray-400">
                      Evaluated {results[activeSet].total_ball_combos?.toLocaleString()} combos | {results[activeSet].total_passed_filters?.toLocaleString()} passed filters
                      {results[activeSet].next_draw_day && ` | Next draw: ${results[activeSet].next_draw_day}`}
                    </p>
                  </div>
                  {constraintSets[activeSet].hml_pattern && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400">HML:</span>
                      <PatternBadge pattern={constraintSets[activeSet].hml_pattern} />
                    </div>
                  )}
                </div>

                {/* Applied Constraints Summary */}
                {results[activeSet].applied_constraints?.length > 0 && (
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-2">
                    {results[activeSet].applied_constraints.map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-[10px]">
                        <span className="font-bold text-blue-700">{c.window}</span>
                        <span className="text-gray-400">{c.label}:</span>
                        <span className="font-mono font-bold text-gray-700">{c.value}</span>
                      </span>
                    ))}
                  </div>
                )}

                <div className="p-4">
                  <TicketTable tickets={results[activeSet].combinations} setKey={activeSet} rangePatterns={results[activeSet].range_patterns} />
                </div>
              </div>
            )}

            {/* Quick summary of all 3 sets */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="all-sets-summary">
              <h3 className="text-sm font-bold text-gray-900 mb-3">All Sets Overview</h3>
              <div className="grid grid-cols-3 gap-3">
                {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => {
                  const s = constraintSets[key];
                  const r = results[key];
                  const n = countActiveConstraints(s);
                  return (
                    <div key={key} className={`rounded-lg p-3 border cursor-pointer transition ${activeSet === key ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
                      onClick={() => setActiveSet(key)} data-testid={`summary-${key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm text-gray-800">{key}</span>
                        {s.hml_pattern && <PatternBadge pattern={s.hml_pattern} />}
                      </div>
                      <p className="text-[10px] text-gray-500">{n} constraint{n !== 1 ? 's' : ''} configured</p>
                      {r?.combinations?.length > 0 && (
                        <p className="text-[10px] font-bold text-green-700 mt-1">{r.combinations.length} tickets generated</p>
                      )}
                      {s.remarks_criteria && <p className="text-[10px] text-gray-400 mt-1 truncate">{s.remarks_criteria}</p>}
                    </div>
                  );
                })}
              </div>
              {drawRef && <p className="text-[10px] text-gray-400 mt-2">Constraints saved for draw #{drawRef}</p>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
